<template>
  <base-view id="service-detail-redesign">
    <div class="service-detail">
      <!-- Hero -->
      <section
        class="hero-section"
        :style="{ backgroundImage: 'url(' + planPhoto(plan.photo) + ')' }"
      >
        <div class="hero-scrim" aria-hidden="true"></div>
        <div class="hero-inner">
          <app-breadcrumbs
            :items="[{ label: 'Services', to: '/services' }, { label: plan.name }]"
            theme="dark"
          />
          <h1>{{ plan.name }}</h1>
          <!-- <p class="hero-tagline">{{ plan.tagline }}</p> -->
        </div>
      </section>

      <!-- Body -->
      <section class="body-section">
        <div class="detail-grid">
          <!-- Main -->
          <div class="main-col">
            <div v-if="plan.quickFacts.length" class="facts-grid">
              <div
                v-for="(fact, i) in plan.quickFacts"
                :key="i"
                class="fact-card"
              >
                <div class="fact-label">{{ fact.label }}</div>
                <div class="fact-value">{{ fact.value }}</div>
              </div>
            </div>

            <a
              v-if="plan.limitsHref"
              :href="plan.limitsHref"
              class="limits-link"
              @click="onContentClick"
              >See {{ plan.limitsLabel }} Limits →</a
            >

            <div class="intro-block" @click="onContentClick">
              <p v-for="(para, i) in plan.intro" :key="i" v-html="para"></p>
            </div>

            <div v-if="plan.eligibility.length" class="eligibility-panel">
              <div class="eligibility-title">{{ plan.eligibilityTitle }}</div>
              <div
                v-for="(req, i) in plan.eligibility"
                :key="i"
                class="eligibility-row"
              >
                <span class="eligibility-check" aria-hidden="true">✓</span>
                <div v-html="req"></div>
              </div>
            </div>

            <!-- Accordion -->
            <div class="accordion-list" @click="onContentClick">
              <div
                v-for="(sec, i) in plan.sections"
                :key="i"
                class="accordion-item"
                :class="{ 'accordion-item-open': openSection === i }"
              >
                <button
                  type="button"
                  class="accordion-header"
                  :aria-expanded="(openSection === i).toString()"
                  :aria-controls="'section-panel-' + planId + '-' + i"
                  :id="'section-header-' + planId + '-' + i"
                  @click="toggleSection(i)"
                >
                  <span class="accordion-title">{{ sec.title }}</span>
                  <span class="accordion-icon" aria-hidden="true">{{
                    openSection === i ? "\u2212" : "+"
                  }}</span>
                </button>
                <transition
                  name="accordion"
                  @enter="onAccordionEnter"
                  @after-enter="onAccordionAfterEnter"
                  @leave="onAccordionLeave"
                >
                  <div
                    v-show="openSection === i"
                    class="accordion-body"
                    :id="'section-panel-' + planId + '-' + i"
                    role="region"
                    :aria-labelledby="'section-header-' + planId + '-' + i"
                  >
                    <template v-for="(blk, j) in sec.blocks">
                      <h3
                        v-if="blk.type === 'h3'"
                        :key="j"
                        class="block-h3"
                        v-html="blk.text"
                      ></h3>
                      <p
                        v-else-if="blk.type === 'p'"
                        :key="j"
                        class="block-p"
                        v-html="blk.text"
                      ></p>
                      <div
                        v-else-if="blk.type === 'note'"
                        :key="j"
                        class="block-note"
                        v-html="blk.text"
                      ></div>
                      <div
                        v-else-if="blk.type === 'list'"
                        :key="j"
                        class="block-list"
                      >
                        <div
                          v-for="(li, k) in blk.items"
                          :key="k"
                          class="block-list-row"
                        >
                          <span
                            class="block-list-dot"
                            aria-hidden="true"
                          ></span>
                          <div v-html="li"></div>
                        </div>
                      </div>
                      <img
                        v-else-if="blk.type === 'img'"
                        :key="j"
                        :src="planPhoto(blk.file)"
                        :alt="blk.alt"
                        class="block-img"
                      />
                      <div
                        v-else-if="blk.type === 'table'"
                        :key="j"
                        class="block-table-wrap"
                      >
                        <table class="block-table">
                          <thead>
                            <tr>
                              <th v-for="(h, hi) in blk.headers" :key="hi">
                                {{ h }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, ri) in blk.rows" :key="ri">
                              <td
                                v-if="row.label"
                                colspan="2"
                                class="row-label"
                              >
                                {{ row.label }}
                              </td>
                              <template v-else>
                                <td>{{ row[0] }}</td>
                                <td>{{ row[1] }}</td>
                              </template>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </template>
                  </div>
                </transition>
              </div>
            </div>

            <!-- Videos -->
            <div v-if="plan.videos.length" class="videos-block">
              <h2>Videos</h2>
              <div class="video-list">
                <div
                  v-for="(vid, i) in plan.videos"
                  :key="i"
                  class="video-card"
                >
                  <iframe
                    :src="vid.src"
                    :title="vid.title"
                    allowfullscreen
                    frameborder="0"
                    scrolling="no"
                  ></iframe>
                  <div class="video-caption">{{ vid.title }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <aside class="sidebar">
            <div v-if="plan.handouts.length" class="side-card">
              <div class="side-label">Handouts</div>
              <a
                v-for="(h, i) in plan.handouts"
                :key="i"
                :href="handoutHref(h.file)"
                target="_blank"
                rel="noopener noreferrer"
                class="handout-link"
              >
                <span class="pdf-chip" aria-hidden="true">PDF</span>
                {{ h.label }}
                <span class="sr-only">(opens in new tab)</span>
              </a>
            </div>

            <div v-if="plan.actions.length" class="side-card side-card-dark">
              <div class="side-label side-label-light">Account access</div>
              <a
                v-for="(act, i) in plan.actions"
                :key="i"
                :href="act.href"
                target="_blank"
                rel="noopener noreferrer"
                class="action-btn"
              >
                {{ act.label }}
                <span class="sr-only">(opens in new tab)</span>
              </a>
            </div>

            <div class="side-card">
              <div class="side-label">Related</div>
              <router-link
                v-if="['fsa', 'hsa', 'dca', 'transit'].includes(planId)"
                :to="{ path: '/whats-covered', query: { plan: planId } }"
                class="side-link"
                >What's Covered</router-link
              >
              <router-link to="/forms" class="side-link"
                >Forms &amp; Documents</router-link
              >
<router-link to="/support" class="side-link"
                >Support &amp; FAQ</router-link
              >
            </div>

            <div class="side-card">
              <div class="side-label">Services</div>
              <router-link
                v-for="p in allPlans"
                :key="p.id"
                :to="'/services/' + p.id"
                class="side-link"
                :class="{ 'side-link-active': p.id === planId }"
                >{{ p.name }}</router-link
              >
            </div>
          </aside>
        </div>
      </section>

      <!-- CTA -->
      <section class="cta-section">
        <div class="cta-card">
          <h2>Questions about {{ plan.shortName }}?</h2>
          <p>
            Our team can walk through plan design, eligibility, and setup with
            you.
          </p>
          <div class="cta-buttons">
            <router-link to="/proposal" class="cta-btn cta-btn-white"
              >Request a Proposal</router-link
            >
            <a href="tel:8887221223" class="cta-btn cta-btn-outline"
              >Call 888.722.1223</a
            >
          </div>
        </div>
      </section>
    </div>
  </base-view>
</template>

<script>
import BaseView from "@/components/layout/BaseView.vue";
import AppBreadcrumbs from "@/components/ui/AppBreadcrumbs.vue";

const p = text => ({ type: "p", text });
const note = text => ({ type: "note", text });
const list = items => ({ type: "list", items });
const h3 = text => ({ type: "h3", text });
const img = (file, alt) => ({ type: "img", file, alt });
const table = (headers, rows) => ({ type: "table", headers, rows });
const LIMITS_HREF = "/support-portal/articles/18310279162131";

const ORDER = [
  "cafeteria",
  "fsa",
  "dca",
  "hsa",
  "hra",
  "transit",
  "wellness",
  "lsa",
  "tuition",
  "cobra"
];

const PLANS = {
  cafeteria: {
    name: "Cafeteria Plans (125 Plans)",
    shortName: "cafeteria plans",
    photo: "cafeteria.jpg",
    tagline:
      "Let employees pay for the benefits they actually want — health insurance, dependent care, and more — with pre-tax dollars.",
    quickFacts: [
      { label: "How It's Taxed", value: "Pre-Tax" },
      { label: "Plan Type", value: "Employee Choice" },
      { label: "Mid-Year Changes", value: "Irrevocable Elections" }
    ],
    intro: [
      'An employer can set up a Cafeteria Plan to reimburse employees for selected medical expenses. These plans are pre-tax benefits for employees and may include:<ul class="inline-ul"><li>Flexible Spending Accounts</li><li>Dependent Care Assistance Programs</li><li>Adoption Assistance Programs</li><li>Premium Conversion Plans</li></ul>',
      "A cafeteria plan is a program that employers offer to help employees pay for certain expenses (like health insurance and dependent care) with pre-tax dollars. Employees get to select which benefits they really want (like ordering a lunch item à la carte instead of being stuck with the full meal combo—hence the name “cafeteria”). Employers like cafeteria plans because, in addition to having happier employees, they also save on taxes."
    ],
    sections: [
      {
        title: "Important features of this plan",
        blocks: [
          p(
            "There may also be state income tax savings. Salary reductions lower earned income, which can impact the earned income credit for eligible taxpayers (though this is not the case in our example). In many cases, that can result in even greater tax savings."
          ),
          p(
            "The amount other employees will save depends on which family members are covered, the premiums for the coverage, the total family income, and the tax deductions and exemptions claimed."
          ),
          note(
            "It is important to know before signing the election forms that employees will not be able to change their benefit elections (i.e., the choices about which benefits to pay for through the cafeteria plan) or salary reduction amounts except at open enrollment and on the occurrence of certain events such as marriage or divorce. This rule is called the “irrevocable election requirement.”"
          ),
          p("Cafeteria Plans are regulated by IRS Section 125.")
        ]
      },
      {
        title: "How does this work in real life?",
        blocks: [
          p(
            "Take the example of Widgets, Inc. John DoGood is the president of Widgets, Inc., a company with 70 employees. One of his new hires, Jane, just came from MegaCo., which has thousands of employees and sponsors a cafeteria plan. She has been asking Mr. DoGood to set up a cafeteria plan for Widgets, Inc., but to be honest, Mr. DoGood does not really know what a cafeteria plan is."
          ),
          p(
            "<em>“What good would this be? Sure, it might make sense for a big company like MegaCo., but Widgets, Inc. is just not in that league. Besides, Widgets, Inc. already provides a solid health insurance plan!”</em>"
          ),
          p(
            "Widgets, Inc. currently pays for 80% of employee-only premiums, and employees pay the remaining 20%. Employees with families pay 100% of the cost of coverage for their spouses and children. The employees’ share of the cost is deducted from their pay on an after-tax basis."
          ),
          p(
            "Could a cafeteria plan work for Widgets, Inc.? Yes! Its employees could easily benefit from a premium-only plan (POP). A POP is the simplest type of cafeteria plan. It is designed for one purpose: to help employees save money by letting them pay for their share of insurance premiums with pre-tax dollars."
          ),
          h3("Premium-Only Plan (POP) Description"),
          p(
            "Mr. DoGood understands now: a POP really makes sense for a business like his. Employees are already making after-tax payroll deductions to pay for their share of insurance costs. Just setting up the POP and telling payroll to take those same deductions on a pre-tax basis means that everyone gets to save money."
          ),
          p(
            "A cafeteria plan helps employers in other ways, as well. In addition to enabling the employer to save on its share of FICA (Social Security and Medicare) and FUTA (federal unemployment) taxes, a cafeteria plan can:"
          ),
          list([
            "help recruit employees and keep them satisfied",
            "increase flexibility to design employee benefits for diverse employee needs",
            "in some states, save on state unemployment insurance and workers’ compensation taxes",
            "cushion the blow of huge premium increases",
            "increase employee awareness of the cost of each benefit"
          ]),
          p(
            "<em>“I like it,”</em> says Mr. DoGood, <em>“but what’s the catch?”</em>"
          ),
          p(
            "Well, the IRS does not give out tax breaks without getting some assurances that they’re being used properly. Widgets, Inc. will have to meet some legal requirements, but this is easy so long as Mr. DoGood gets good legal advice in setting up and operating his plan."
          ),
          p(
            "<em>“That’s what I was worried about,”</em> says Mr. DoGood. <em>“Attorneys! I’m not MegaCo., you know, I have a small budget and I can’t afford more administrative staff right now.”</em>"
          ),
          p(
            "No worries. The POP has minimal set-up costs. Just tell the payroll folks what to do and how to explain the plan to employees. Someone also needs to handle the distribution and collection of election forms. A third-party administrator, like Rocky Mountain Reserve, can help with these tasks and Widgets, Inc. will still save money!"
          )
        ]
      },
      {
        title: "How much will his employees really save on taxes?",
        blocks: [
          p(
            "Take Jane as an example. She is married and has one child. The company pays for 80% of Jane’s own health insurance but nothing for her family. She pays $6,400 in premiums ($700 for her share of the employee-only premium, plus $5,700 for family coverage) under Widgets, Inc.’s health insurance plan. In 2020, Jane earns $75,000 and her husband (a student) earns no income. They file a joint tax return."
          ),
          table(
            ["POP Cafeteria Plan", "No Cafeteria Plan"],
            [
              ["$75,000", "$75,000"],
              { label: "1. Adjusted Gross Income" },
              ["($6,400)", "$0"],
              { label: "2. Cafeteria Plan Salary Reductions" },
              ["$68,600", "$75,000"],
              { label: "3. W-2 Gross Wages" },
              ["($11,400)", "($11,400)"],
              { label: "4. Standard Deduction" },
              ["($10,950)", "($10,950)"],
              { label: "5. Exemptions" },
              ["$46,250", "$52,650"],
              { label: "6. Taxable Income (lines 3, 4, 5)" },
              ["$68,600", "$75,000"],
              { label: "7. W-2 Gross Wages" },
              ["($6,100)", "($7,060)"],
              { label: "8. Federal Income Tax (line 6 @ tax schedule)" },
              ["($5,248)", "($5,738)"],
              { label: "9. FICA Tax (7.65% of line 3)" },
              ["$0", "($6,400)"],
              { label: "10. After-Tax Premium Payments" },
              ["$57,252", "$55,802"],
              {
                label:
                  "11. Pay After Taxes and Premium Payments (lines 7, 8, 9, 10)"
              }
            ]
          ),
          p(
            "Jane would save about $1,450 in taxes (line 11, col. 1 minus line 11, col. 2) by paying for her health insurance premiums under the POP. A shortcut way to determine Jane’s savings is to multiply 22.65% by her $6,400 of salary reductions (22.65% = her 15% marginal tax rate plus 7.65% for FICA)."
          ),
          p(
            "Widgets, Inc. also saves on taxes. For example, it saves $490 in FICA employment taxes (i.e., 7.65% × $6,400 of salary reductions)."
          ),
          p(
            "No wonder Jane wants a cafeteria plan—it results in significant tax savings. There are some other bonuses as well:"
          ),
          list([
            "employees pay only for the benefits they really want (i.e., they can design their benefits “à la carte”)",
            "often, more benefits are offered than might otherwise be available without a cafeteria plan"
          ])
        ]
      }
    ],
    handouts: []
  },

  // Limits current as of 2026 (IRS Rev. Proc. for 2026 FSA/fringe benefit limits).
  // Refresh again once the IRS releases 2027 inflation-adjusted limits.
  fsa: {
    name: "Flexible Spending Account (FSA)",
    shortName: "FSAs",
    photo: "fsa.jpg",
    tagline:
      "Pay medical deductibles, copays, and out-of-pocket treatments with pre-tax dollars — the full annual election is available from day one.",
    limitsHref: LIMITS_HREF,
    limitsLabel: "FSA",
    quickFacts: [
      { label: "How It's Taxed", value: "Pre-Tax" },
      { label: "Unused Funds", value: "Use It or Lose It" },
      { label: "Funding", value: "Employer Front-Loaded" }
    ],
    intro: [
      "Employers may offer a health Flexible Spending Account (FSA) as part of their employee benefits programs. The FSA is funded front loaded by the employer and the employee’s salary reduction repays the employer for the agreed amount. This money can be used to reimburse employees for expenses related to medical insurance deductibles and copays, out-of-pocket medical treatments or supplies, and dependent healthcare expenses.",
      "When employees contribute to a flexible spending arrangement, no employment or federal income taxes are deducted from the contribution. Any contributions to an FSA by an employer are excluded from the employee’s gross income which reduces the tax payments of the employer. <u>Both employee and employer save money when using an FSA!</u>"
    ],
    sections: [
      {
        title: "Simple example of how pre-tax works",
        blocks: [
          p(
            "Pre-tax dollars mean you get access to the funds before social security, federal and state tax, etc. Say you work 10 hours at $10 ($100) and you wanted to set aside $25 for eligible expenses. Instead of getting taxed on the full $100, you would only get taxed on the $75 since the $25 was taken out before!"
          ),
          p(
            "Average taxes in the United States are about 20%, if you were taxed on the $100 that totals $20 of taxes. If you were taxed on only the $75 that totals only $15 of taxes. The yearly maximum for an FSA is $3,400. If you had a family FSA and made $50,000 annually- you would only be taxed $9,320 compared to the full $10,000."
          ),
          note(
            "If you did not set funds aside, you would pay an extra $680 of taxes by waiting to pay for your share of medical expenses after-tax."
          )
        ]
      },
      {
        title: "Important features of the plan",
        blocks: [
          p("All FSAs must meet certain legal requirements:"),
          p(
            "Only expenses that are incurred during the plan year can be reimbursed. “Incurred” means the date of service or purchase, it is not based on when the expenses are paid. For example, an expense is incurred in December 2025 when an individual visits the medical practitioner, not when the practitioner’s bill is received or paid by the patient in January 2026. Only 2025 funds could be used to pay for this visit."
          ),
          note(
            "Any money remaining in a flexible spending account at the end of the plan year cannot be carried over to the next year. The only exception is if the employer allows for the 2025 IRS maximum rollover of $660. The 2026 rollover maximum is $680."
          ),
          p(
            "If an employee terminated with their employer, they would have access to the funds via claims for expenses incurred from the first day of the plan year to the date of termination. Card transactions end on the date of termination. Payroll contributions end with the former employee’s last paycheck."
          ),
          p(
            "Employees are not required to have any other insurance to participate. Unfortunately, self-employed persons cannot have a Health FSA."
          ),
          p(
            "An FSA is allowed under the Internal Revenue Code Section 125 and covers all pre-tax dollars for non-reimbursed medical, dental and dependent care expenses. Other items that may be covered include insurance premiums, deductibles, co-payments, and non-covered services."
          ),
          p(
            'Click <a href="/whats-covered" class="inline-link">here</a> to see a full list of eligible items and required documentation to make the items eligible!'
          ),
          img("ElevateCard.png", "Card Image"),
          h3("What is the benefit of a Debit Card?"),
          list([
            "It conveniently helps the participant with cash flow",
            "The card pays directly to the provider at the point of sale",
            "It adds a convenience to accessing participant funds"
          ]),
          p(
            "Not all merchants have their card machine coded properly for the card to be approved. Transactions may not be automatically approved upon swiping your card at locations such as Target or Wal-greens that sell both eligible and ineligible items. Documentation requests may still occur for eligible items or services."
          )
        ]
      }
    ],
    handouts: [
      { label: "FSA User Guide", file: "2026 Health FSA User Guide.pdf" },
      {
        label: "LP FSA User Guide",
        file: "2026 Limited Purpose Health FSA User Guide.pdf"
      }
    ],
    videos: [
      {
        title: "Introduction to Flexible Spending Accounts",
        src:
          "https://fast.wistia.net/embed/iframe/jn0h9tael7?seo=false&playerColor=096843"
      },
      {
        title: "Introduction to Limited Purpose FSAs",
        src:
          "https://fast.wistia.net/embed/iframe/97bsjir1cp?seo=false&playerColor=096843"
      }
    ]
  },

  dca: {
    name: "Dependent Care Assistance Program",
    shortName: "dependent care accounts",
    photo: "dca.jpg",
    tagline:
      "Reimburse employment-related dependent care expenses — daycare, preschool, before- and after-school care — with pre-tax dollars.",
    limitsHref: LIMITS_HREF,
    limitsLabel: "DCAP",
    quickFacts: [
      { label: "How It's Taxed", value: "Pre-Tax" },
      { label: "Annual Maximum", value: "$7,500 (Family)" },
      { label: "Rollover", value: "Never Rolls Over" }
    ],
    intro: [
      "DCAP stands for Dependent Care Assistance Program and is also a Flexible Spending Account. An employee can use a DCAP to be reimbursed for employment-related expenses that allow the employee and his or her spouse to be “gainfully employed.” Employment-related expenses apply only to certain individuals.",
      "A cafeteria plan lets participants elect coverage under a DCAP with an annual limit of up to $7,500. They pay for that coverage with pre-tax salary reduction dollars. At the end of each month, the DCAP reimburses the dependent care expenses that occurred during the month. Assume that Jane elects the full $7,500 of coverage. She must reduce her taxable wages by $7,500 (the annual premium) to pay for the coverage. By participating in the DCAP, Jane would achieve tax savings but subject to additional factors that affect the calculation of the tax savings."
    ],
    sections: [
      {
        title: "Important features of the plan",
        blocks: [
          note(
            "DCAPs are not front loaded like a typical FSA, which means that the available funds will grow as contributions are made throughout the plan year. Not all daycare providers accept benefits cards, some expenses will have to be paid out of pocket and then reimbursed through a claim."
          ),
          p(
            "Who qualifies as a “dependent” for which the funds can be used? DCAP expenses could consist of having a babysitter or day-care provider take care of an employee’s child (under the age of 13) while Mom and Dad are both working, <strong>or taking care of a spouse or other tax dependent who lives with the employee and is incapable of self-care.</strong>"
          ),
          p("Some services will not qualify, such as overnight camps.")
        ]
      }
    ],
    handouts: [
      {
        label: "DCA User Guide",
        file: "2026 Dependent Care FSA User Guide.pdf"
      }
    ],
    videos: [
      {
        title: "Introduction to Dependent Care Assistance Programs",
        src:
          "https://fast.wistia.net/embed/iframe/qm5id14mng?seo=false&playerColor=096843"
      }
    ]
  },

  // Limits current as of 2026 (IRS Rev. Proc. 2025-19).
  // Refresh again once the IRS releases 2027 inflation-adjusted limits.
  hsa: {
    name: "Health Savings Account (HSA)",
    shortName: "HSAs",
    photo: "hsa.jpg",
    tagline:
      "A tax-exempt account for qualified medical expenses — yours to keep, with funds that roll over year after year.",
    limitsHref: LIMITS_HREF,
    limitsLabel: "HSA",
    quickFacts: [
      { label: "Fund Expiration", value: "No Deadline" },
      { label: "How It's Taxed", value: "Tax-Free (Qualified Expenses)" },
      { label: "Ownership", value: "Portable" }
    ],
    intro: [
      "A Health Savings Account (HSA) is a tax-exempt trust or custodial account established exclusively for the purpose of paying qualified medical expenses for beneficiaries covered under a qualified High Deductible Health Plan (HDHP). With an HSA, you can pay for current and future eligible medical, dental, and vision expenses. The funds can be withheld through payroll deduction, and therefore employees do not pay federal, state, or social security taxes. The employer also saves the matching FICA taxes.",
      "An HDHP costs less than traditional health care plans due to the higher deductible, so the money saved on premiums can be added to your HSA."
    ],
    eligibilityTitle: "In order to open an HSA, you:",
    eligibility: [
      "Must be covered by a qualifying High Deductible Health Plan (HDHP)",
      "Must not be covered by any other health plan that is not an HDHP",
      "Must not be enrolled in Medicare",
      "Must not be claimed as a dependent on another persons tax return"
    ],
    sections: [
      {
        title: "Simple example of how pre-tax works",
        blocks: [
          p(
            "Pre-tax dollars mean you get access to the funds before social security, federal and state tax, etc. Say you work 10 hours at $10 ($100) and you wanted to set aside $25 for eligible expenses. Instead of getting taxed on the full $100, you would only get taxed on the $75 since the $25 was taken out before!"
          ),
          p(
            "Average taxes in the United States are about 20%, if you were taxed on the $100 that totals $20 of taxes. If you were taxed solely on the $75, that totals only $15 of taxes. The yearly maximum contribution amount for an HSA is $4,400 for an individual or $8,750 for a family. If you had a family HSA and made $50,000 annually- you would only be taxed $8,250 compared to the full $10,000."
          ),
          note(
            "If you did not set funds aside, you would pay an extra $1,750 of taxes by waiting to pay for your share of medical expenses after-tax."
          )
        ]
      },
      {
        title: "Accessing HSA Funds/Investments",
        blocks: [
          p(
            "You own and control the funds in your HSA. The money you contribute to your HSA is federally tax-deductible, whether or not you itemize deductions. Your funds can be conveniently accessed by a debit card to pay for medical expenses."
          ),
          img("ElevateCardImage.png", "Card Image"),
          list([
            "It conveniently helps the participant with cash flow",
            "The card pays directly to the provider at the point of sale",
            "It adds a convenience to accessing participant funds"
          ])
        ]
      },
      {
        title: "Important features of the plan",
        blocks: [
          note(
            "If you use the funds for expenses other than health care-related expenses, you must pay income tax plus a 20% penalty. Federal limits dictate how much can be contributed to an HSA each year. It also provides a $1,000 catch-up provision for individuals over age 55 (not enrolled in Medicare). After reaching age 65, the funds may also be used for non-medical related expenses. You will only have to pay income tax on the withdrawals at that point."
          ),
          p(
            "If you begin an HSA with an employer and you leave the employer, your funds are not lost and can move along with you. The difference is that you become responsible for the monthly fees to keep the account open and a new card/account will be created for you independent of the former employer."
          )
        ]
      }
    ],
    handouts: [{ label: "HSA User Guide", file: "2027 HSA User Guide.pdf" }],
    videos: [
      {
        title: "HSA/FSA/HRA Comparison",
        src:
          "https://fast.wistia.net/embed/iframe/narmv3kr4s?seo=false&playerColor=096843"
      }
    ]
  },

  hra: {
    name: "Health Reimbursement Arrangement (HRA)",
    shortName: "HRAs",
    photo: "hra.jpg",
    tagline:
      "Employer-funded reimbursement for deductibles and out-of-pocket health costs — with carryover set by plan design.",
    quickFacts: [
      { label: "Funding", value: "Employer-Funded" },
      { label: "Rollover", value: "Varies by Plan" },
      { label: "Eligible Expenses", value: "Employer Sets Expenses" }
    ],
    intro: [
      "An HRA may be funded only by employer contributions. Money in the account may be used to reimburse employees for deductible payments and other out-of-pocket health costs (including spouse and dependents). Funds in a Health Reimbursement Account may roll over to the next year according to the policies of the employer.",
      "<strong>What Are the Tax Advantages of an HRA?</strong> If an HRA is designed and operated in compliance with IRS guidance, employees will not be taxed on the value of their HRA coverage or on reimbursements that they receive from the HRA, under Code §105 and §106, even though unused amounts may carry forward into future years for reimbursement of medical expenses. The employer will, subject to certain conditions and limitations, be entitled to a deduction for reimbursements made under the HRA."
    ],
    sections: [
      {
        title: "Example: Health Reimbursement Account",
        blocks: [
          p(
            "Here is an example of how an HRA might work in conjunction with a common high-deductible health coverage (HDHC) arrangement."
          ),
          p(
            "An employer has an insured plan with high-deductible health coverage (HDHC), but with first-dollar coverage for certain preventive services such as flu shots, mammograms, well-child checkups, and annual physical exams. The employer pays 50% of the HDHC premium, and employees pay the other 50% with pre-tax salary reductions under a cafeteria plan."
          ),
          p(
            "Employees who participate in the HDHC also participate in an employer-funded HRA with a $1,000 annual limit that can be used to pay for out-of-pocket medical expenses (e.g., co-pays, deductibles, and medical expenses that are not covered by the HDHC). Expenses submitted to the HRA may qualify for negotiated provider discounts like the discounts available under the HDHC. Unused HRA amounts can be carried over from year to year for reimbursement of medical expenses. The HRA is not part of the cafeteria plan."
          ),
          p(
            "The health plan in the example above is a hybrid arrangement combining features of traditional health care with consumer-driven health care (CDHC) features. It is designed to heighten employee awareness of health care costs and reduce unwise expenditures by having employees bear the cost of their medical care (i.e., they will have to dip into their HRAs) up to the HDHC deductible amount. Proponents of a consumer-driven approach claim that it will reduce overall health costs by making employees better health care consumers. Others say that it penalizes individuals who have greater health care needs."
          ),
          p(
            "An HRA can be coupled with HDHC as in the above example, or it can be offered on a stand-alone basis. When a stand-alone HRA is offered, an employee would not be required to participate in HDHC in order to be eligible to participate in the HRA."
          )
        ]
      }
    ],
    videos: [
      {
        title: "Introduction to Health Reimbursement Arrangements",
        src:
          "https://fast.wistia.net/embed/iframe/k4nqf77rxp?seo=false&playerColor=096843"
      }
    ]
  },

  transit: {
    name: "Qualified Transit & Parking Accounts",
    shortName: "commuter accounts",
    photo: "transit.jpg",
    tagline:
      "Reimburse eligible transit, vanpooling, and work parking expenses up to the IRS statutory limit — with pre-tax dollars.",
    quickFacts: [
      { label: "How It's Taxed", value: "Pre-Tax" },
      { label: "Unused Funds", value: "Rolls Over Monthly" }
    ],
    limitsHref: LIMITS_HREF,
    limitsLabel: "Transit",
    intro: [
      "The purpose of this pre-tax employee benefits account is to reimburse employees for eligible transit and parking expenses, up to the IRS statutory limit.",
      "Under general principles of taxation, all fringe benefits provided to an employee by his or her employer (including transportation plans) are taxable to the employee unless the Code provides a specific exclusion for the benefit. For most transportation fringe benefits, the maximum amount that employees can exclude from income is subject to a statutory limit that is periodically adjusted for inflation.",
      'Specific regulations by the IRS on Commuter Benefits can be found <a href="https://www.law.cornell.edu/cfr/text/26/1.132-9" class="inline-link">here</a>. One of these regulations indicates that <strong>all claims must be made within 180 days of incurred expenses.</strong>'
    ],
    eligibilityTitle:
      "Since 1985, employers have been permitted to give the following transportation fringe benefits to employees on a tax-free basis:",
    eligibility: [
      "qualified parking;",
      "transit passes;",
      'transportation in a commuter highway vehicle if such transportation is in connection with travel between the employee\u2019s residence and place of employment (also known as <a href="https://rmr.zendesk.com/hc/en-us/articles/360036254613-How-can-I-use-my-Commuter-Benefit-Transit-Account-with-Uber-and-Lyft-" class="inline-link">\u201cvanpooling\u201d</a> like Uber or Lyft).'
    ],
    sections: [],
    videos: [
      {
        title: "Introduction to Commuter Benefit Accounts",
        src:
          "https://fast.wistia.net/embed/iframe/5pfymqwdww?seo=false&playerColor=096843"
      }
    ],
    handouts: [
      {
        label: "Transit User Guide",
        file: "2026 Qualified Transit User Guide.pdf"
      },
      {
        label: "Parking User Guide",
        file: "2026 Qualified Parking User Guide.pdf"
      }
    ]
  },

  wellness: {
    name: "Wellness Accounts & Disease Management",
    shortName: "wellness accounts",
    photo: "wellness.jpg",
    tagline:
      "Employer-sponsored incentives that promote employee health and activity-based programs — unique to every employer.",
    quickFacts: [
      { label: "Funding", value: "Employer-Funded" },
      { label: "Focus", value: "Preventive" },
      { label: "Plan Design", value: "Varies by Employer" }
    ],
    intro: [
      "Employer-sponsored Wellness Accounts and Disease Management Programs are offered as an incentive to promote employee health and activity-based programs. These accounts are unique to every employer."
    ],
    sections: [
      {
        title: "Wellness Accounts",
        blocks: [
          p(
            "Wellness programs can take many forms and may not even be called a “wellness program.” They may cover only employees and/or other family members. A comprehensive employer-sponsored wellness programs might offer a system of coordinated health-related communications, assessments, and incentives intended to raise health awareness and promote positive health behaviors."
          ),
          p(
            "Wellness programs might also use one, some, or all of the following tools to promote wellness and decrease health plan costs:"
          ),
          list([
            "health-related newsletters and informative material",
            "lunch-hour sessions on first aid, self-care, or healthy eating",
            "health risk assessments of willing employees",
            "personal consultations with case managers regarding nutrition, lifestyle changes, and related issues",
            "blood pressure and cholesterol screening",
            "optional or compulsory smoking-cessation programs",
            "weight-loss programs",
            "flu shots or other vaccinations",
            "on-site gyms or other exercise facilities",
            "payment of health club dues",
            "diagnostic testing; and",
            "financial incentives to encourage participation or achievement of specific wellness standards"
          ]),
          p(
            "Some offer rewards to employees who participate or impose penalties on those who do not. Rewards for participation can take many forms:"
          ),
          list([
            "Cash or cash equivalents (e.g., gift cards or gift certificates)",
            "Health benefit plan-related rewards (e.g., reductions or waivers in employee cost-sharing features, such as premiums, co-payments, or deductibles, or additional employer-provided benefits)",
            "Other non cash rewards (e.g., employee discounts or merchandise prizes)."
          ]),
          p("Penalties of non-participation can take many forms:"),
          list([
            "Financial penalties",
            "Refusal to hire",
            "Disqualification from the employer’s health plan",
            "Termination of employment (note, however, that HIPAA or ADA issues may be raised when a program involves penalties for non-participation)."
          ])
        ]
      },
      {
        title: "Disease-Management Program",
        blocks: [
          p(
            "An employer-sponsored disease-management program generally identifies employees and their family members who have, or who are at risk for developing, certain chronic medical conditions. A typical disease-management program will identify these individuals and “coach” them on how to manage their conditions."
          ),
          p("Disease-management programs might provide:"),
          list([
            "Evidence-based medical information",
            "Disease-specific support",
            "Case monitoring",
            "Coordination of the care and treatment provided by the employer’s group health plan.",
            "Monitor laboratory or other medical test results",
            "Provide telephonic, e-mail, or web-based reminders of treatment or appointment schedules",
            "Furnish information about minimizing health risks."
          ]),
          p(
            "Sometimes, these programs offer rewards (such as reductions or waivers of employee cost-sharing items, such as premiums, co-payments, or deductibles) for employees who participate."
          )
        ]
      },
      {
        title:
          "How Are Wellness and Disease-Management Programs Alike (and Different)?",
        blocks: [
          p(
            "In some ways, employer-sponsored wellness and disease-management programs may appear quite similar—both types of programs are intended to help participants lead healthier lives and lower or control health care costs. In fact, sometimes employers combine wellness and disease-management programs into a single program, or coordinate wellness and disease-management programs with each other or with the employer’s other health plans, such as a major medical plan, health FSA, HRA, or HSA."
          ),
          p(
            "But wellness and disease-management programs typically differ from one another. Wellness programs generally are designed to help all employees become and stay healthier, while disease-management programs are targeted toward helping only those employees who are at risk for developing, or who have already developed, chronic medical conditions."
          )
        ]
      }
    ],
    handouts: []
  },

  lsa: {
    name: "Lifestyle Spending Account (LSA)",
    shortName: "LSAs",
    photo: "lifestyle.jpg",
    tagline:
      "Post-tax funds employees can spend on what matters most — fitness, home office, education, even pet care.",
    quickFacts: [
      { label: "How It's Taxed", value: "Post-Tax (Taxable)" },
      { label: "Eligible Expenses", value: "Broad / Employer-Set" },
      { label: "Plan Design", value: "Employer-Defined" }
    ],
    intro: [
      "Lifestyle Spending Accounts (LSAs) present a compelling option for employers seeking to bolster their workforce's well-being. LSAs typically provide employees with a predetermined sum for designated expenditures — taxable reimbursements for activities promoting physical wellness such as gym memberships and fitness gadgets, or financial wellness undertakings like estate planning costs and financial advisor fees."
    ],
    sections: [
      {
        title: "What is a lifestyle spending account?",
        blocks: [
          p(
            "The Rocky Mountain Reserve Lifestyle Spending Account gives you the flexibility to create personalized benefit programs for employees. A convenient debit card makes it easy for them to spend on what matters most."
          ),
          p(
            "LSAs are a creative way to build a supportive culture and to attract, engage, and retain top talent. With a wide range of spending categories — from home office goods to fitness to education, or even pet care — you can offer a benefit tailored to your unique workforce and business needs."
          )
        ]
      },
      {
        title:
          "What are the benefits of offering a lifestyle spending account to your employees?",
        blocks: [
          p(
            "You are most likely considering a lifestyle spending account because you are looking for additional benefit options for a changing and varied workforce. Offering a lifestyle spending account is a way for you to reinforce and promote your company culture, meet the market demand and round out your benefits package with an account that delivers personalized spending opportunities for the things that matter most to employees."
          )
        ]
      },
      {
        title: "How does a lifestyle spending account work?",
        blocks: [
          p(
            "Lifestyle spending accounts are a way for you to offer yearly, monthly, or other periodic post-tax dollars to your employees to use for spending beyond traditional consumer directed healthcare (CDH) accounts. You designate the amount you want to contribute per employee and determine any limits you would like to set for specific service types within the available spending categories. Your employees can then access the funds in the form of a convenient debit card."
          )
        ]
      },
      {
        title: "Employer Resources",
        blocks: [
          list([
            '<a href="https://rmr.zendesk.com/hc/en-us/articles/13444362576147-Exploring-the-Tax-Implications-of-Lifestyle-Spending-Accounts-LSA-for-Employers" class="inline-link">Exploring the Tax Implications of Lifestyle Spending Accounts (LSA) for Employers</a>'
          ])
        ]
      }
    ],
    handouts: [{ label: "LSA FAQs", file: "RMR LSA FAQs.pdf" }]
  },

  tuition: {
    name: "Tuition Reimbursement",
    shortName: "tuition reimbursement",
    photo: "tuition.jpg",
    tagline:
      "Provide educational assistance to employees on a tax-free basis — up to $5,250 per year under IRS Code §127.",
    quickFacts: [
      { label: "Annual Maximum", value: "Varies by employer" },
      { label: "Tax code", value: "IRS Code §127" },
      { label: "Cafeteria plan", value: "Not permitted" }
    ],
    intro: [
      "IRS Code §127 allows employers to provide certain educational assistance to their employees on a tax-free basis, meaning that amounts paid or incurred by an employer under a qualified educational assistance program are deductible by the employer and are excludable from the employees’ taxable income. Tuition assistance programs cannot be offered through a cafeteria plan.",
      "The maximum amount of educational assistance benefits that can be provided to an employee on a tax-free basis each year is $5,250. A qualified educational assistance program can provide assistance to employees for a broad range of educational expenses, including expenses for graduate-level courses and for courses that are not job-related. Consequently, employers establishing these programs have a wide range of plan design choices."
    ],
    eligibilityTitle:
      "To qualify under Code §127, an educational assistance program must:",
    eligibility: [
      "provide benefits exclusively to employees of the employer;",
      "provide only qualified educational assistance benefits;",
      "be a separate written program established by an employer;",
      "does not allow employees a choice between assistance and cash;",
      "does not discriminate in favor of highly compensated employees;",
      "does not provide more than 5% of benefits in a year to more-than-5% owners."
    ],
    sections: [],
    handouts: []
  },

  cobra: {
    name: "COBRA Administration",
    shortName: "COBRA",
    photo: "cobra.jpg",
    tagline:
      "Continuation coverage for employees who lose health benefits through termination or another qualifying event.",
    quickFacts: [
      { label: "Election Window", value: "60 Days" },
      { label: "Duration", value: "Up to 18–36 Months" },
      { label: "Cost", value: "Participant Pays Full Premium" }
    ],
    intro: [
      "COBRA is a federal law appearing in three places: the Employee Retirement Income Security Act (ERISA); the Internal Revenue Code (Code); and the Public Health Service Act (PHSA).",
      'These laws allow qualifying employees who lose their health benefits through termination or a “qualifying event” to continue with the group health plan for a limited time. Coverage can be cancelled anytime by emailing the request to <a href="mailto:cobra@rmrbenefits.com" class="inline-link">cobra@rmrbenefits.com</a>. This request can be submitted at any time of year but must be submitted by the end of the month for the following month as coverage cannot end part way through a month.'
    ],
    sections: [
      {
        title: "What Plans Are Subject to COBRA?",
        blocks: [
          p(
            "Group Health Plans: COBRA applies to nearly all plans that provide medical care and are maintained by the employer. The term “medical care” has a broad definition in the law. It includes medical, dental, vision, and drug treatment coverage."
          ),
          p("Plans Subject To COBRA:"),
          list([
            "plans for which an HMO provides the medical services;",
            "group insurance plans in which employees pay the premiums;",
            "treatment programs and clinics maintained by employers;",
            "self-insured medical reimbursement plans;",
            "employee assistance programs;",
            "health flexible spending accounts;",
            "health reimbursement arrangements (HRAs);",
            "discount programs; and",
            "wellness programs."
          ]),
          p(
            "Example: Health and Life Insurance. An employer covers all employees under a medical plan and group term life insurance. When an employee quits, he or she may elect to continue coverage under the medical plan. The employee has no right under COBRA to continue group term life insurance because it is not medical care. (Note, however, that some state laws give employees the right to continue group term life insurance)."
          )
        ]
      },
      {
        title: "Important features of the plan",
        blocks: [
          p(
            "Certain plans that provide for long-term care services are not considered group health plans and are not subject to COBRA – even when they provide for benefits that include medical care."
          ),
          p(
            "Some Voluntary Plans Are Subject to COBRA. Some employers offer their employees voluntary employee-pay-all medical insurance programs, in which the employers have minimal involvement (such as allowing the insurer to contact employees during working hours, collecting employee premiums and remitting them to the insurer, and similar activities). These arrangements can be subject to COBRA, and employers should get advice before encouraging or allowing them; otherwise, employers can be left with COBRA obligations and no corresponding insurance coverage."
          )
        ]
      }
    ],
    handouts: [
      { label: "Employee Guide to COBRA", file: "Employee Guide to COBRA.pdf" }
    ],
    actions: [
      { label: "COBRA Login", href: "https://cobrapoint.benaissance.com/" },
      {
        label: "COBRA Registration",
        href: "https://cobrapoint.benaissance.com/Registration/Identify"
      }
    ]
  }
};

export default {
  name: "ServiceDetailRedesign",
  components: { BaseView, AppBreadcrumbs },
  data() {
    return {
      openSection: 0
    };
  },
  computed: {
    planId() {
      const id = this.$route.params.plan;
      return PLANS[id] ? id : "fsa";
    },
    plan() {
      const raw = PLANS[this.planId];
      return {
        name: raw.name,
        shortName: raw.shortName,
        photo: raw.photo,
        tagline: raw.tagline,
        limitsHref: raw.limitsHref || "",
        limitsLabel: raw.limitsLabel || "",
        quickFacts: raw.quickFacts || [],
        intro: raw.intro || [],
        eligibilityTitle: raw.eligibilityTitle || "",
        eligibility: raw.eligibility || [],
        sections: raw.sections || [],
        handouts: raw.handouts || [],
        actions: raw.actions || [],
        videos: raw.videos || []
      };
    },
    allPlans() {
      return ORDER.map(id => ({ id, name: PLANS[id].name }));
    }
  },
  watch: {
    planId() {
      this.openSection = 0;
    }
  },
  methods: {
    toggleSection(i) {
      this.openSection = this.openSection === i ? -1 : i;
    },
    onContentClick(e) {
      const link = e.target.closest("a");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (href.startsWith("/")) {
        e.preventDefault();
        this.$router.push(href);
      }
    },
    planPhoto(filename) {
      return require(`@/assets/${filename}`);
    },
    handoutHref(filename) {
      return require(`@/assets/forms/${filename}`);
    },
    onAccordionEnter(element) {
      const width = getComputedStyle(element).width;
      element.style.width = width;
      element.style.position = "absolute";
      element.style.visibility = "hidden";
      element.style.height = "auto";
      const height = getComputedStyle(element).height;
      element.style.width = null;
      element.style.position = null;
      element.style.visibility = null;
      element.style.height = 0;
      getComputedStyle(element).height; // force repaint
      const duration = (Math.floor(parseInt(height) / 100) + 1) * 0.15;
      setTimeout(() => {
        element.style.transition = `height ${duration}s ease-in-out`;
        element.style.height = height;
      });
    },
    onAccordionAfterEnter(element) {
      element.style.height = "auto";
    },
    onAccordionLeave(element) {
      const height = getComputedStyle(element).height;
      element.style.height = height;
      getComputedStyle(element).height; // force repaint
      const duration = (Math.floor(parseInt(height) / 100) + 1) * 0.15;
      setTimeout(() => {
        element.style.transition = `height ${duration}s ease-in-out`;
        element.style.height = 0;
      });
    }
  }
};
</script>

<style scoped>
.service-detail {
  background: #ffffff;
}

/* ── Hero ── */
.hero-section {
  position: relative;
  height: 260px;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}
.hero-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(20, 24, 30, 0.35),
    rgba(20, 24, 30, 0.82)
  );
}
.hero-inner {
  position: relative;
  max-width: 1120px;
  margin: 0 auto;
  height: 100%;
  padding: 0 24px 28px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.hero-inner h1 {
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 8px;
  color: #ffffff;
  text-wrap: pretty;
}
.hero-tagline {
  font-size: 15.5px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  max-width: 600px;
  line-height: 1.5;
}

/* ── Body ── */
.body-section {
  padding: 40px 24px 72px;
}
.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 44px;
  max-width: 1120px;
  margin: 0 auto;
}
@media (max-width: 900px) {
  .detail-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .sidebar {
    position: static !important;
  }
}
.main-col {
  min-width: 0;
}

/* ── Limits link ── */
.limits-link {
  display: inline-block;
  font-size: 15px;
  font-weight: 700;
  color: #096843;
  margin-bottom: 20px;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.limits-link:hover {
  text-decoration: underline;
}
.limits-link:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
}

/* ── Quick facts ── */
.facts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 32px;
}
.fact-card {
  padding: 16px 18px;
  background: #ffffff;
  border: 1px solid #e2e2e2;
  border-radius: 12px;
}
.fact-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b6b6b;
  margin-bottom: 6px;
}
.fact-value {
  font-size: 14.5px;
  font-weight: 700;
  color: #171717;
  line-height: 1.4;
}

/* ── Intro ── */
.intro-block {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 36px;
}
.intro-block p {
  font-size: 15.5px;
  line-height: 1.75;
  color: #363636;
  margin: 0;
  text-wrap: pretty;
}
.intro-block ::v-deep .inline-link,
.block-p ::v-deep .inline-link,
.block-note ::v-deep .inline-link,
.block-list ::v-deep .inline-link,
.eligibility-row ::v-deep .inline-link {
  color: #096843;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.intro-block ::v-deep .inline-link:hover,
.block-p ::v-deep .inline-link:hover,
.block-note ::v-deep .inline-link:hover,
.block-list ::v-deep .inline-link:hover,
.eligibility-row ::v-deep .inline-link:hover {
  color: #06502f;
}

/* ── Eligibility panel ── */
.eligibility-panel {
  padding: 24px 26px;
  border-radius: 14px;
  background: #f1fdf5;
  border: 1px solid #bfe9cf;
  margin-bottom: 36px;
}
.eligibility-title {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #096843;
  margin-bottom: 14px;
}
.eligibility-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 10px;
}
.eligibility-row:last-child {
  margin-bottom: 0;
}
.eligibility-check {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #096843;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  margin-top: 2px;
}
.eligibility-row div {
  font-size: 14.5px;
  line-height: 1.6;
  color: #363636;
}

/* ── Accordion ── */
.accordion-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.accordion-item {
  background: #ffffff;
  border: 1px solid #e2e2e2;
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.2s ease;
}
.accordion-item-open {
  border-color: #a9dfbe;
}
.accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 20px 24px;
  cursor: pointer;
  background: none;
  border: none;
  text-align: left;
  font: inherit;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.accordion-header:hover {
  background: #fafafa;
}
.accordion-header:focus-visible {
  outline: 2px solid #096843;
  outline-offset: -2px;
}
.accordion-title {
  font-weight: 700;
  font-size: 16px;
  color: #171717;
  line-height: 1.35;
  text-wrap: pretty;
}
.accordion-icon {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: #eef1f0;
  color: #4a4a4a;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}
.accordion-item-open .accordion-icon {
  background: #096843;
  color: #ffffff;
}
.accordion-body {
  padding: 0 24px 24px;
  border-top: 1px solid #eeeeee;
  overflow: hidden;
}
.accordion-body > * {
  margin-top: 18px;
}
.accordion-body > *:first-child {
  margin-top: 18px;
}
.block-h3 {
  font-size: 15px;
  font-weight: 700;
  color: #171717;
  margin: 0;
}
.block-p {
  font-size: 14.5px;
  line-height: 1.7;
  color: #3d3d3d;
  margin: 0;
  text-wrap: pretty;
}
.intro-block ::v-deep .inline-ul,
.block-list ::v-deep .inline-ul {
  margin: 10px 0 0;
  padding-left: 20px;
  list-style: disc;
}
.intro-block ::v-deep .inline-ul li,
.block-list ::v-deep .inline-ul li {
  font-size: 15.5px;
  line-height: 1.6;
  margin-bottom: 4px;
}
.block-note {
  padding: 14px 16px;
  border-radius: 10px;
  background: #f1fdf5;
  font-size: 14px;
  line-height: 1.6;
  color: #1f5c39;
  font-weight: 600;
}
.block-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.block-list-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.block-list-dot {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: #2e8b57;
  flex: none;
  margin-top: 8px;
}
.block-list-row div {
  font-size: 14.5px;
  line-height: 1.6;
  color: #3d3d3d;
}
.block-img {
  display: block;
  max-width: 220px;
  height: auto;
  border-radius: 8px;
}
.block-table-wrap {
  overflow-x: auto;
}
.block-table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  font-size: 13.5px;
}
.block-table th {
  background: #171717;
  color: #ffffff;
  padding: 10px 8px;
  font-weight: 700;
}
.block-table th:first-child {
  border-top-left-radius: 8px;
}
.block-table th:last-child {
  border-top-right-radius: 8px;
}
.block-table td {
  padding: 8px;
  border: 1px solid #e2e2e2;
  border-top: none;
  color: #3d3d3d;
}
.block-table tr:nth-child(even) td {
  background: #f7f7f7;
}
.block-table .row-label {
  text-align: left;
  font-size: 12.5px;
  background: transparent !important;
  border-left: none;
  border-right: none;
  padding-top: 4px;
  padding-bottom: 4px;
  color: #6b6b6b;
}

/* ── Videos ── */
.videos-block {
  margin-top: 40px;
}
.videos-block h2 {
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0 0 16px;
  color: #171717;
}
.video-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.video-card {
  background: #ffffff;
  border: 1px solid #e2e2e2;
  border-radius: 14px;
  overflow: hidden;
}
.video-card iframe {
  width: 100%;
  height: 280px;
  display: block;
  border: 0;
}
.video-caption {
  padding: 14px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #363636;
}

/* ── Sidebar ── */
.sidebar {
  position: sticky;
  top: 96px;
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.side-card {
  background: #ffffff;
  border: 1px solid #e2e2e2;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.side-label {
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b6b6b;
  margin-bottom: 4px;
}
.side-label-light {
  color: rgba(255, 255, 255, 0.85);
}
.side-card-dark {
  background: #096843;
}
.handout-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 13px;
  border-radius: 9px;
  background: #f6f6f6;
  font-size: 13.5px;
  font-weight: 600;
  color: #303030;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.handout-link:hover {
  background: #eaf9ef;
  color: #096843;
}
.handout-link:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
}
.pdf-chip {
  font-size: 10px;
  font-weight: 800;
  padding: 3px 6px;
  border-radius: 5px;
  background: #fbe1dc;
  color: #9c2f19;
  flex: none;
}
.action-btn {
  display: block;
  text-align: center;
  padding: 11px 14px;
  border-radius: 9px;
  background: #ffffff;
  color: #096843;
  font-weight: 700;
  font-size: 13.5px;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.action-btn:hover {
  opacity: 0.9;
}
.action-btn:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}
.side-link {
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: #303030;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.side-link:hover {
  background: #eaf9ef;
  color: #096843;
}
.side-link-active,
.side-link-active:hover {
  background: #096843;
  color: #ffffff;
}
.side-link:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
}

/* ── CTA ── */
.cta-section {
  padding: 0 24px 80px;
}
.cta-card {
  max-width: 1120px;
  margin: 0 auto;
  text-align: center;
  padding: 44px;
  border-radius: 20px;
  background: linear-gradient(135deg, #096843, #1c4d78);
}
.cta-card h2 {
  margin: 0 0 10px;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #ffffff;
}
.cta-card p {
  margin: 0 0 24px;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
}
.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}
.cta-btn {
  padding: 13px 24px;
  border-radius: 9px;
  font-weight: 700;
  font-size: 14.5px;
  text-decoration: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.15s ease;
}
.cta-btn:hover {
  opacity: 0.85;
}
.cta-btn:focus-visible {
  outline: 2px solid #ffffff;
  outline-offset: 2px;
}
.cta-btn-white {
  background: #ffffff;
  color: #096843;
}
.cta-btn-outline {
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
